import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as deliveryConfigsApi from '../api/deliveryConfigs';

export function DeliveryConfigs() {
  const { selectedOrg } = useOrg();
  const { isAdmin } = usePermissions();
  const [configs, setConfigs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [activeTab, setActiveTab] = useState('configs');
  const [form, setForm] = useState({
    name: '',
    host: '',
    port: 22,
    username: '',
    password: '',
    remotePath: '/',
    filenameTemplate: '{reportName}_{date}.csv',
    programId: '',
    profileId: ''
  });

  const loadData = useCallback(async () => {
    if (!selectedOrg || !isAdmin) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [configsData, logsData] = await Promise.all([
        deliveryConfigsApi.getDeliveryConfigs(selectedOrg.id),
        deliveryConfigsApi.getDeliveryLogs(selectedOrg.id, { limit: 20 })
      ]);
      setConfigs(configsData);
      setLogs(logsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, isAdmin]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenCreate = () => {
    setEditingConfig(null);
    setForm({
      name: '',
      host: '',
      port: 22,
      username: '',
      password: '',
      remotePath: '/',
      filenameTemplate: '{reportName}_{date}.csv',
      programId: '',
      profileId: ''
    });
    setShowModal(true);
  };

  const handleOpenEdit = (config) => {
    setEditingConfig(config);
    setForm({
      name: config.name,
      host: config.host,
      port: config.port,
      username: config.username,
      password: '',
      remotePath: config.remotePath,
      filenameTemplate: config.filenameTemplate,
      programId: config.programId || '',
      profileId: config.profileId || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!selectedOrg || saving) return;
    setSaving(true);
    setError(null);
    try {
      const data = { ...form };
      if (!data.password) delete data.password;
      if (!data.programId) delete data.programId;
      if (!data.profileId) delete data.profileId;

      if (editingConfig) {
        await deliveryConfigsApi.updateDeliveryConfig(selectedOrg.id, editingConfig.id, data);
      } else {
        await deliveryConfigsApi.createDeliveryConfig(selectedOrg.id, data);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (configId) => {
    if (!selectedOrg || !window.confirm('Delete this delivery configuration?')) return;
    try {
      await deliveryConfigsApi.deleteDeliveryConfig(selectedOrg.id, configId);
      loadData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTest = async (configId) => {
    if (!selectedOrg) return;
    setTesting(configId);
    try {
      const result = await deliveryConfigsApi.testDeliveryConfig(selectedOrg.id, configId);
      alert(result.success ? 'Connection test successful!' : 'Connection test failed');
      loadData();
    } catch (err) {
      alert('Connection test failed: ' + err.message);
    } finally {
      setTesting(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return { bg: '#c6f6d5', color: '#276749' };
      case 'failed': return { bg: '#fed7d7', color: '#c53030' };
      case 'pending': return { bg: '#fefcbf', color: '#975a16' };
      default: return { bg: '#e2e8f0', color: '#4a5568' };
    }
  };

  if (!isAdmin) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You need admin permissions to manage delivery configurations.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="SFTP Delivery Configuration"
        subtitle="Configure automated report and export delivery"
      />

      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('configs')}
          style={activeTab === 'configs' ? styles.activeTab : styles.tab}
          data-testid="configs-tab"
        >
          Configurations
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          style={activeTab === 'logs' ? styles.activeTab : styles.tab}
          data-testid="logs-tab"
        >
          Delivery Logs
        </button>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading...</div>
      ) : activeTab === 'configs' ? (
        <>
          <div style={styles.toolbar}>
            <button onClick={handleOpenCreate} style={styles.createButton} data-testid="add-config-button">
              + Add SFTP Configuration
            </button>
          </div>

          <div style={styles.tableContainer}>
            <table style={styles.table} data-testid="configs-table">
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Host</th>
                  <th style={styles.th}>Username</th>
                  <th style={styles.th}>Remote Path</th>
                  <th style={styles.th}>Filename Template</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {configs.map(config => (
                  <tr key={config.id} style={styles.tr} data-testid={`config-${config.id}`}>
                    <td style={styles.td}><strong>{config.name}</strong></td>
                    <td style={styles.td}>{config.host}:{config.port}</td>
                    <td style={styles.td}>{config.username}</td>
                    <td style={styles.td}>{config.remotePath}</td>
                    <td style={styles.td}><code style={styles.code}>{config.filenameTemplate}</code></td>
                    <td style={styles.td}>
                      <button
                        onClick={() => handleTest(config.id)}
                        disabled={testing === config.id}
                        style={styles.testButton}
                        data-testid={`test-${config.id}`}
                      >
                        {testing === config.id ? 'Testing...' : 'Test'}
                      </button>
                      <button
                        onClick={() => handleOpenEdit(config)}
                        style={styles.editButton}
                        data-testid={`edit-${config.id}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(config.id)}
                        style={styles.deleteButton}
                        data-testid={`delete-${config.id}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {configs.length === 0 && (
                  <tr>
                    <td colSpan="6" style={styles.emptyState}>No delivery configurations. Click "Add SFTP Configuration" to create one.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="logs-table">
            <thead>
              <tr>
                <th style={styles.th}>Time</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Filename</th>
                <th style={styles.th}>Remote Host</th>
                <th style={styles.th}>Bytes</th>
                <th style={styles.th}>Error</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const statusStyle = getStatusColor(log.status);
                return (
                  <tr key={log.id} style={styles.tr}>
                    <td style={styles.td}>{new Date(log.startedAt).toLocaleString()}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.statusBadge, background: statusStyle.bg, color: statusStyle.color }}>
                        {log.status}
                      </span>
                    </td>
                    <td style={styles.td}>{log.filename}</td>
                    <td style={styles.td}>{log.remoteHost}</td>
                    <td style={styles.td}>{log.bytesTransferred}</td>
                    <td style={styles.td}>{log.error || '-'}</td>
                  </tr>
                );
              })}
              {logs.length === 0 && (
                <tr>
                  <td colSpan="6" style={styles.emptyState}>No delivery logs yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal} data-testid="config-modal">
            <h3 style={styles.modalTitle}>
              {editingConfig ? 'Edit SFTP Configuration' : 'Add SFTP Configuration'}
            </h3>
            <div style={styles.formGroup}>
              <label style={styles.label}>Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                style={styles.input}
                placeholder="e.g., Production Reports"
                data-testid="name-input"
              />
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Host *</label>
                <input
                  type="text"
                  value={form.host}
                  onChange={(e) => setForm(prev => ({ ...prev, host: e.target.value }))}
                  style={styles.input}
                  placeholder="sftp.example.com"
                  data-testid="host-input"
                />
              </div>
              <div style={styles.formGroupSmall}>
                <label style={styles.label}>Port</label>
                <input
                  type="number"
                  value={form.port}
                  onChange={(e) => setForm(prev => ({ ...prev, port: parseInt(e.target.value) }))}
                  style={styles.input}
                  data-testid="port-input"
                />
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Username *</label>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => setForm(prev => ({ ...prev, username: e.target.value }))}
                  style={styles.input}
                  data-testid="username-input"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Password</label>
                <input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm(prev => ({ ...prev, password: e.target.value }))}
                  style={styles.input}
                  placeholder={editingConfig ? '(unchanged)' : ''}
                  data-testid="password-input"
                />
              </div>
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Remote Path</label>
              <input
                type="text"
                value={form.remotePath}
                onChange={(e) => setForm(prev => ({ ...prev, remotePath: e.target.value }))}
                style={styles.input}
                placeholder="/reports"
                data-testid="remote-path-input"
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Filename Template</label>
              <input
                type="text"
                value={form.filenameTemplate}
                onChange={(e) => setForm(prev => ({ ...prev, filenameTemplate: e.target.value }))}
                style={styles.input}
                placeholder="{reportName}_{date}.csv"
                data-testid="filename-template-input"
              />
              <small style={styles.hint}>Variables: {'{reportName}'}, {'{date}'}, {'{timestamp}'}, {'{profileId}'}</small>
            </div>
            <div style={styles.modalActions}>
              <button onClick={() => setShowModal(false)} style={styles.cancelButton}>
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name || !form.host || !form.username}
                style={styles.submitButton}
                data-testid="save-config-button"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  tabs: { display: 'flex', gap: '0.5rem', marginBottom: '1rem' },
  tab: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  activeTab: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#ebf8ff', color: '#3182ce', fontSize: '0.875rem', cursor: 'pointer' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  toolbar: { display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' },
  createButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #38a169', background: '#38a169', color: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  code: { background: '#f7fafc', padding: '0.125rem 0.25rem', borderRadius: '4px', fontSize: '0.75rem' },
  testButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #38a169', background: 'white', color: '#38a169', fontSize: '0.75rem', cursor: 'pointer', marginRight: '0.25rem' },
  editButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #3182ce', background: 'white', color: '#3182ce', fontSize: '0.75rem', cursor: 'pointer', marginRight: '0.25rem' },
  deleteButton: { padding: '0.25rem 0.5rem', borderRadius: '4px', border: '1px solid #e53e3e', background: 'white', color: '#e53e3e', fontSize: '0.75rem', cursor: 'pointer' },
  statusBadge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'capitalize' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: '8px', padding: '1.5rem', minWidth: '500px', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' },
  modalTitle: { margin: '0 0 1.5rem 0', fontSize: '1.25rem', fontWeight: '600' },
  formGroup: { marginBottom: '1rem', flex: 1 },
  formGroupSmall: { marginBottom: '1rem', width: '100px' },
  formRow: { display: 'flex', gap: '1rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#4a5568' },
  input: { width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem' },
  hint: { display: 'block', marginTop: '0.25rem', fontSize: '0.75rem', color: '#718096' },
  modalActions: { display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' },
  cancelButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #e2e8f0', background: 'white', fontSize: '0.875rem', cursor: 'pointer' },
  submitButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #38a169', background: '#38a169', color: 'white', fontSize: '0.875rem', cursor: 'pointer' }
};
